import { postViewIntent } from '@/api/mutations'
import { useChatPageContext } from '@/components/chat/useChatPageContext'
import { encodeSalaParam, planViewIntent, type ViewIntent } from '@/lib/viewIntent'
import { useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'

/** CDV-029: serialised intents larger than this go to the server stash
 *  and the URL becomes `?sala=ref:<id>` instead of inlining the JSON. */
export const SALA_INLINE_LIMIT = 6000

/**
 * Build the callback that ChatSidebar fires when the assistant emits a
 * ⟦VIEW⟧ marker (CDV-001). Lives here (not in ChatSidebar) so the
 * sidebar itself stays router-free and trivially testable; AppShell owns
 * the wiring.
 *
 * Behaviour per plan:
 * - `invalid` (missing site_id from portfolio context) → console.warn, no nav.
 * - `navigate` → forward to the named route verbatim.
 * - `sala` → push current location with `?sala=<json>` (or `?sala=ref:<id>`
 *   when the JSON exceeds SALA_INLINE_LIMIT) so <AppShell> mounts the sala
 *   on top of the current page. The browser back and the view shell's ←
 *   button both unwind via `router.history.back()`.
 */
export function useViewIntentDispatcher() { //@usedby:AppShell.tsx
  const router = useRouter()
  const ctx = useChatPageContext()
  return useCallback(
    async (intent: ViewIntent) => {
      const plan = planViewIntent(intent, ctx)
      switch (plan.type) {
        case 'invalid':
          console.warn('[viewIntent] dropped:', plan.reason, intent)
          return
        case 'navigate':
          router.navigate({
            to: plan.to,
            params: plan.params,
            search: plan.search,
          } as never)
          return
        case 'sala': {
          const json = encodeSalaParam(plan.intent)
          // 15 Oct 2026 (Dori): evidence photos from the chat open in the
          // photo side panel over the CURRENT page (Zoe's panel stays as
          // it was). Closing it returns to that page, not to a full-page
          // photo grid. Other view kinds still use the full-page sala.
          if (
            plan.intent.kind === 'photos' &&
            Array.isArray(plan.intent.event_ids) &&
            plan.intent.event_ids.length > 0 &&
            json.length <= SALA_INLINE_LIMIT
          ) {
            router.navigate({
              to: router.state.location.pathname,
              resetScroll: false,
              search: (prev: Record<string, unknown>) => ({ ...prev, evidence: json }),
            } as never)
            return
          }
          if (json.length <= SALA_INLINE_LIMIT) {
            router.navigate({
              to: router.state.location.pathname,
              search: (prev: Record<string, unknown>) => ({ ...prev, sala: json }),
            } as never)
            return
          }
          // Composed payloads larger than the threshold get stashed
          // server-side; the URL carries only the short id.
          try {
            const { id } = await postViewIntent(json)
            router.navigate({
              to: router.state.location.pathname,
              search: (prev: Record<string, unknown>) => ({
                ...prev,
                sala: `ref:${id}`,
              }),
            } as never)
          } catch (e) {
            console.warn('[viewIntent] stash failed; dropping intent', e)
          }
          return
        }
      }
    },
    [router, ctx],
  )
}
